import { useEffect, useState } from "react";
import { ICON_BASE_PATH } from "@/app/utils/config";

import "./Notifications.scss";

import {
  Checkbox,
  type CheckboxChangeEvent,
} from "@progress/kendo-react-inputs";

import Threshold, { type ThresholdValue } from "./Threshold";
import {
  gxgrowxpreferencecreateApi,
  gxgrowxPreferenceListApi,
} from "@/app/apiConfig/services/customerAbstractApiLayer";
import SuccessMessage from "@/app/components/webloading/errorHandling/SuccessMessage";
import RequiredMessage from "@/app/components/webloading/errorHandling/RequiredMessage";

import { t } from "i18next";
import NoDataFound from "@/app/components/common/warning/NoDataFound";

// ---------- Types matching the API response ----------
interface ApiTemplate {
  templateRecordId: number;
  eventName: string;
  smsTemplateRecordId: number;
  emailTemplateRecordId: number;
  appTemplateRecordId: number;
  smsCheckBox: "Y" | "N";
  emailCheckBox: "Y" | "N";
  inAppCheckBox: "Y" | "N";
}

interface ApiGroup {
  groupName: string;
  templateList: ApiTemplate[];
}

interface ApiCategory {
  notifyCategoryId: number;
  categoryName: string;
  smsCheckBox: "Y" | "N";
  emailCheckBox: "Y" | "N";
  inAppCheckBox: "Y" | "N";
  checkBox: "Y" | "N";
  groups: ApiGroup[];
}

interface ApiResponse {
  data: ApiCategory[];
  status: number;
  errorCode: string;
  errorMessage: string;
}

// ---------- Types the UI works with ----------
interface ChannelSettings {
  sms: boolean;
  email: boolean;
  inApp: boolean;
}

interface Item {
  appTemplateRecordId: any;
  emailTemplateRecordId: any;
  smsTemplateRecordId: any;
  id: string;
  label: string;
  channels: ChannelSettings;
  availableChannels: (keyof ChannelSettings)[];
}

interface Section {
  id: string;
  title: string;
  items: Item[];
}

interface Tile {
  id: string;
  title: string;
  isTileOpen: boolean;
  channels: (keyof ChannelSettings)[];
  sections: Section[];
}

// ---------- Threshold-specific state shape ----------
type ThresholdType = "lower" | "upper";

interface ThresholdState {
  amount: string;
  currencyCode: string;
}

const THRESHOLD_LABELS: Record<ThresholdType, string> = {
  lower: "Balance below threshold",
  upper: "Balance above threshold",
};

const DEFAULT_DENOMINATION = "100";

const channels: Record<keyof ChannelSettings, string> = {
  sms: "SMS",
  email: "Email",
  inApp: "In-App",
};

const transformApiResponse = (res: ApiResponse): Tile[] => {
  return res.data.map((category) => ({
    id: String(category.notifyCategoryId),
    title: category.categoryName,
    isTileOpen: false,

    channels: ["sms", "email", "inApp"],

    sections: category.groups
      .filter((g) => g.templateList.length > 0)
      .map((group, idx) => ({
        id: `${category.notifyCategoryId}-${idx}-${group.groupName}`,

        title: group.groupName,

        items: group.templateList.map((tpl) => ({
          id: String(tpl.templateRecordId),

          label: tpl.eventName,

          smsTemplateRecordId: tpl.smsTemplateRecordId,
          emailTemplateRecordId: tpl.emailTemplateRecordId,
          appTemplateRecordId: tpl.appTemplateRecordId,

          channels: {
            sms: tpl.smsCheckBox === "Y",
            email: tpl.emailCheckBox === "Y",
            inApp: tpl.inAppCheckBox === "Y",
          },
          availableChannels: [
            ...(Number(tpl.smsTemplateRecordId) !== 0 ? ["sms"] : []),
            ...(Number(tpl.emailTemplateRecordId) !== 0 ? ["email"] : []),
            ...(Number(tpl.appTemplateRecordId) !== 0 ? ["inApp"] : []),
          ] as (keyof ChannelSettings)[],
        })),
      })),
  }));
};

// ---------- Component ----------

const NotificationSubHeader = (userData: any) => {
  const [data, setData] = useState<Tile[]>([]);
  const [isThresholdOpen, setIsThresholdOpen] = useState(false);
  const [thresholdType, setThresholdType] = useState<ThresholdType>("lower");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  console.log(data, "data");

  const [thresholdValues, setThresholdValues] = useState<
    Record<ThresholdType, ThresholdState | undefined>
  >({ lower: undefined, upper: undefined });

  const openThreshold = (type: ThresholdType) => {
    setThresholdType(type);
    setIsThresholdOpen(true);
  };

  const closeThreshold = () => setIsThresholdOpen(false);

  const handleThresholdSave = (value: ThresholdValue) => {
    setThresholdValues((prev: any) => ({
      ...prev,
      [value.type]: {
        amount: value.amount,
        currencyCode: value.currencyCode,
      },
    }));
  };

  const onChange = (updatedData: Tile[]) => setData(updatedData);

  const handleOnToggleOpen = (tileId: string) => {
    setData((prevData) =>
      prevData.map((tile) => ({
        ...tile,
        isTileOpen: tile.id === tileId ? !tile.isTileOpen : false,
      })),
    );
  };

  const itemsMap = (
    section: Section,
    field: keyof ChannelSettings,
    value: CheckboxChangeEvent,
  ): Section => ({
    ...section,
    items: section.items.map((item) =>
      item.availableChannels?.includes(field)
        ? {
            ...item,
            channels: { ...item.channels, [field]: value.value },
          }
        : item,
    ),
  });

  const handleOnCheckAll = (
    headerId: string,
    field: keyof ChannelSettings,
    value: CheckboxChangeEvent,
  ) => {
    onChange(
      data.map((tile) =>
        tile.id === headerId
          ? {
              ...tile,
              sections: tile.sections.map((section) =>
                itemsMap(section, field, value),
              ),
            }
          : tile,
      ),
    );
  };

  const handleOnItemChannelChange = (
    headerId: string,
    sectionId: string,
    itemId: string,
    field: keyof ChannelSettings,
  ) => {
    onChange(
      data.map((tile) =>
        tile.id === headerId
          ? {
              ...tile,
              sections: tile.sections.map((section) =>
                section.id === sectionId
                  ? {
                      ...section,
                      items: section.items.map((item) =>
                        item.id === itemId &&
                        item.availableChannels?.includes(field)
                          ? {
                              ...item,
                              channels: {
                                ...item.channels,
                                [field]: !item.channels[field],
                              },
                            }
                          : item,
                      ),
                    }
                  : section,
              ),
            }
          : tile,
      ),
    );
  };

  const isAllChecked = (tile: Tile, channel: keyof ChannelSettings) =>
    tile.sections.every((section) =>
      section.items.every(
        (item) =>
          !item.availableChannels?.includes(channel) || item.channels[channel],
      ),
    );

  const isChannelAvailableInTile = (
    tile: Tile,
    channel: keyof ChannelSettings,
  ) =>
    tile.sections.some((section) =>
      section.items.some((item) => item.availableChannels?.includes(channel)),
    );

  const allChannelList = (tile: Tile) =>
    tile.channels.map((channel) => {
      const isAvailableInTile = isChannelAvailableInTile(tile, channel);
      return (
        <div key={channel} className="gx-all-channel-list">
          <label>{channels[channel]}</label>
          <Checkbox
            checked={isAvailableInTile ? isAllChecked(tile, channel) : false}
            disabled={!isAvailableInTile}
            onClick={(e) => e.stopPropagation()}
            onChange={(value) => handleOnCheckAll(tile.id, channel, value)}
          />
        </div>
      );
    });

  const channelList = (tile: Tile, item: Item, sectionId: string) =>
    tile.channels.map((channel) => {
      const isAvailable = item.availableChannels?.includes(channel);
      return (
        <div key={channel} className="gx-channel-list">
          <label>{channels[channel]}</label>
          <Checkbox
            checked={isAvailable ? item.channels[channel] : false}
            disabled={!isAvailable}
            onClick={(e) => e.stopPropagation()}
            onChange={() =>
              handleOnItemChannelChange(tile.id, sectionId, item.id, channel)
            }
          />
        </div>
      );
    });

  const fetchCheckboxListApi = async (userData: any) => {
    const userId = userData?.userData?.userId || "";
    try {
      const response: any = await gxgrowxPreferenceListApi({
        id: userId,
      });

      if (response?.data) {
        setData(transformApiResponse(response));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const toCreatePayload = (tiles: Tile[]) => {
    return tiles
      .map((tile) => ({
        notifyCategoryId: Number(tile.id),
        categoryName: tile.title,
        groups: tile.sections
          .map((section) => ({
            groupName: section.title,
            templateList: section.items
              .filter(
                (item) =>
                  item.channels.sms ||
                  item.channels.email ||
                  item.channels.inApp,
              )
              .map((item) => {
                const isLower = item.label === THRESHOLD_LABELS.lower;
                const isUpper = item.label === THRESHOLD_LABELS.upper;
                const tv = isLower
                  ? thresholdValues.lower
                  : isUpper
                    ? thresholdValues.upper
                    : undefined;

                return {
                  eventName: item.label,
                  smsTemplateRecordId: item.smsTemplateRecordId,
                  emailTemplateRecordId: item.emailTemplateRecordId,
                  appTemplateRecordId: item.appTemplateRecordId,
                  smsCheckBox: item.channels.sms ? "Y" : "N",
                  emailCheckBox: item.channels.email ? "Y" : "N",
                  inAppCheckBox: item.channels.inApp ? "Y" : "N",
                  ...((isLower || isUpper) && {
                    thresholdAmount: tv?.amount ?? "",
                    currencyCode2: tv?.currencyCode ?? "",
                    // thresholdDenomination: DEFAULT_DENOMINATION,
                  }),
                };
              }),
          }))
          .filter((group) => group.templateList.length > 0),
      }))
      .filter((tile) => tile.groups.length > 0);
  };

  const handleUpdate = async () => {
    try {
      setSuccessMessage("");
      setErrorMessage("");
      const payload = toCreatePayload(data);
      const response: any = await gxgrowxpreferencecreateApi(payload);
      if (response.status === 1) {
        setSuccessMessage("Notifications Settings updated successfully.");
      } else if (response.status === 0) {
        setErrorMessage("Failed to update Notifications Settings.");
      }
    } catch (error: any) {
      console.error(error);
      setErrorMessage(error?.message);
    }
  };

  useEffect(() => {
    if (userData) {
      fetchCheckboxListApi(userData);
    }
  }, [userData]);

  useEffect(() => {
    if (successMessage || errorMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage("");
        setErrorMessage("");
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [successMessage, errorMessage]);

  return (
    <>
      {data?.length === 0 ? (
        <>
          <NoDataFound
            iconType={1}
            className="gx-no-data"
            message={t("no_data_found")}
          />
        </>
      ) : (
        <>
          <div className="gx-notification-box">
            {data?.map((tile, index) => (
              <div
                key={tile.id}
                className={`${index === data.length - 1 ? "" : "gx-bottom-border"} ${
                  index === 0 ? "" : "gx-top-space"
                }`}
              >
                <div className="gx-notification-sub-header-container">
                  <button
                    onClick={() => handleOnToggleOpen(tile.id)}
                    className="gx-notification-sub-header"
                  >
                    <div className="gx-notification-left">
                      <img
                        src={`${ICON_BASE_PATH}down_arrow1.svg`}
                        alt=""
                        className={
                          tile.isTileOpen
                            ? "gx-down-arrow-icon-notification-sub-header-open"
                            : "gx-down-arrow-icon-notification-sub-header-close"
                        }
                      />
                      <h5>{tile.title}</h5>
                    </div>

                    <div className="gx-notification-right">
                      {allChannelList(tile)}
                    </div>
                  </button>
                </div>

                <div
                  className={`gx-tail-main-wrapper ${
                    tile.isTileOpen
                      ? "gx-tail-main-wrapper-open"
                      : "gx-tail-main-wrapper-close"
                  }`}
                >
                  <div className="gx-tail-main-container">
                    {tile.sections.map((section) => (
                      <div key={section.id} className="gx-notification-sub">
                        <h5>{section.title}</h5>

                        {section.items.map((item) => (
                          <div key={item.id} className="gx-section-item">
                            <p className="gx-section-item-name">
                              {item.label}

                              {(item.label === THRESHOLD_LABELS.lower ||
                                item.label === THRESHOLD_LABELS.upper) && (
                                <button
                                  className="gx-threshold-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openThreshold(
                                      item.label === THRESHOLD_LABELS.lower
                                        ? "lower"
                                        : "upper",
                                    );
                                  }}
                                >
                                  <img
                                    src={`${ICON_BASE_PATH}thresholdsetting.svg`}
                                    alt="setting"
                                  />{" "}
                                  Threshold
                                </button>
                              )}
                            </p>

                            <div className="gx-section-item-check-box-container">
                              {channelList(tile, item, section.id)}
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-2">
            {successMessage && <SuccessMessage message={successMessage} />}
            {errorMessage && <RequiredMessage message={errorMessage} />}
          </div>

          <div className="gx-update-child">
            <button onClick={handleUpdate}>Update</button>
          </div>
        </>
      )}

      <Threshold
        isOpen={isThresholdOpen}
        onClose={closeThreshold}
        type={thresholdType}
        initialAmount={thresholdValues[thresholdType]?.amount}
        initialCurrencyCode={thresholdValues[thresholdType]?.currencyCode}
        onSave={handleThresholdSave}
      />
    </>
  );
};

export default NotificationSubHeader;
